import React, { memo, useCallback, useMemo } from "react";
import { Dropdown } from "@evergis/ui";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getPrintLegendPlacement, getPrintLegendPosition } from "../../../../ducks/print/selectors";
import { setLegend } from "../../../../ducks/print/print";
import { useAction } from "../../../../hooks/useAction";
import { LegendPosition, PrintType } from "../../../../interfaces/print";

export const LegendPositionSetting = memo(() => {
  const { t } = useTranslation("print");

  const legendPositionOptions = useMemo(
    () => [
      {
        value: LegendPosition.Left,
        text: t("lefthand"),
        enable: (placement: PrintType) => placement === PrintType.MapWithLegend,
      },
      {
        value: LegendPosition.Right,
        text: t("righthand"),
        enable: (placement: PrintType) => placement === PrintType.MapWithLegend,
      },
      {
        value: LegendPosition.Under,
        text: t("bottom"),
        enable: (placement: PrintType) => placement === PrintType.MapWithLegend,
      },
      {
        value: LegendPosition.Vertical,
        text: t("vertical"),
        enable: (placement: PrintType) => placement === PrintType.MapAndLegend,
      },
      {
        value: LegendPosition.Horizontal,
        text: t("horizontal"),
        enable: (placement: PrintType) => placement === PrintType.MapAndLegend,
      },
    ],
    [t],
  );

  const getPositionOptionsByPlacement = useCallback(
    (printType: PrintType) => legendPositionOptions.filter(option => option.enable(printType)),
    [legendPositionOptions],
  );

  const updateLegend = useAction(setLegend);
  const legendPlacement = useSelector(getPrintLegendPlacement);
  const legendPosition = useSelector(getPrintLegendPosition);
  const processedLegendOptions = useMemo(() => getPositionOptionsByPlacement(legendPlacement), [legendPlacement]);
  const changeLegendPosition = useCallback(
    ([{ value }]: typeof legendPositionOptions) => updateLegend({ position: value }),
    [updateLegend],
  );

  return (
    <Dropdown
      label={t("legendPosition")}
      width={"19.5rem"}
      zIndex={120}
      value={legendPosition}
      options={processedLegendOptions}
      onChange={changeLegendPosition}
    />
  );
});
